import { FormConfig } from '../types';

export const formConfigs: FormConfig = {
  versirent: {
    fields: [
      {
        name: 'appName',
        label: 'Application Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your application name',
        helperText: 'This will be the name displayed to your users',
        helpText: 'Choose a clear, memorable name that represents your app. This will appear in app stores and on user devices.',
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'logoUrl',
        label: 'Company Logo',
        type: 'file',
        required: true,
        helperText: 'Upload your company logo (PNG, JPG, SVG up to 5MB)',
        helpText: 'Upload your company logo in high resolution. This will be used throughout the app and in marketing materials.',
        validation: {
          fileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
          maxFileSize: 5 * 1024 * 1024, // 5MB
        },
      },
      {
        name: 'useLogoAsIcon',
        label: 'Use logo as app icon',
        type: 'checkbox',
        required: false,
        helperText: 'Check this box to use your company logo as the app icon',
      },
      {
        name: 'appIconUrl',
        label: 'App Icon',
        type: 'file',
        required: false, // Will be made conditional based on useLogoAsIcon checkbox
        helperText: 'Upload your app icon (PNG, JPG, SVG up to 2MB)',
        helpText: 'Upload a square app icon that represents your app. This will appear on user devices and in app stores. Only required if you are not using your logo as the app icon.',
        validation: {
          fileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
          maxFileSize: 2 * 1024 * 1024, // 2MB
        },
      },

      {
        name: 'primaryColor',
        label: 'Primary Brand Color',
        type: 'color',
        required: true,
        helperText: 'Select your primary brand color',
        helpText: 'Choose your main brand color. This will be used for buttons, links, and primary UI elements throughout the app.',
      },
      {
        name: 'secondaryColor',
        label: 'Secondary Brand Color',
        type: 'color',
        required: false,
        helperText: 'Select your secondary brand color (optional)',
      },
      {
        name: 'companyDescription',
        label: 'Company Description',
        type: 'textarea',
        required: false,
        placeholder: 'Tell us about your company',
        helperText: 'Optional: Brief description of your company',
        validation: {
          maxLength: 500,
        },
      },
      {
        name: 'copyrightMsg',
        label: 'Copyright Message',
        type: 'text',
        required: false,
        placeholder: '© Copyright 2024',
        helperText: 'Copyright message for your app (defaults to current year)',
        helpText: 'Enter a copyright message for your app. If left blank, it will default to "© Copyright [current year]".',
        validation: {
          maxLength: 100,
        },
      },
      {
        name: 'ecommerceType',
        label: 'E-commerce Solution',
        type: 'select',
        required: true,
        options: ['integrated', 'custom_in_app', 'custom_browser'],
        helperText: 'Choose how you want to handle e-commerce in your app',
        helpText: 'Integrated Shop provides a complete e-commerce solution, while custom options let you use your existing website.',
      },
      {
        name: 'redirectWebsiteUrl',
        label: 'Custom Shop URL',
        type: 'url',
        required: false, // Will be made conditional based on checkbox
        placeholder: 'yourwebsite.com or https://yourwebsite.com',
        helperText: 'Your website URL that will be used for shop features',
        helpText: 'This URL will be used for shop redirects and must be a valid URL. Only required if you checked the box above.',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+)',
        },
        conditional: {
          dependsOn: 'ecommerceType',
          showWhen: true,
        },
      },
      {
        name: 'termsUrl',
        label: 'Terms and Conditions',
        type: 'url',
        required: true,
        placeholder: 'https://example.com/terms',
        helperText: 'URL to your terms and conditions page or upload document',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+|file:.+)',
        },
      },
      {
        name: 'privacyUrl',
        label: 'Privacy Policy',
        type: 'url',
        required: true,
        placeholder: 'https://example.com/privacy',
        helperText: 'URL to your privacy policy page or upload document',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+|file:.+)',
        },
      },
      {
        name: 'versiRentStoreId',
        label: 'VersiRent Store ID',
        type: 'text',
        required: false,
        placeholder: 'Enter VersiRent store ID',
        helperText: 'Store ID for your VersiRent test account (optional)',
        helpText: 'Enter the Store ID associated with your VersiRent test account. This helps identify which store/location the test account is for.',
        validation: {
          minLength: 1,
          maxLength: 50,
        },
      },
      {
        name: 'versiRentUsername',
        label: 'VersiRent Test Account Username',
        type: 'text',
        required: true,
        placeholder: 'Enter VersiRent test username',
        helperText: 'Username for VersiRent test account',
        helpText: 'Enter the username for your VersiRent test account. This account will be used by Ideal staff and App Store reviewers to login and review the app. Test accounts MUST: (1) Have a balance owed greater than $0.00, (2) Must be a RTO or Club. The account should be kept active for as long as possible to ensure accessibility to reviewers and testers.',
        validation: {
          minLength: 3,
          maxLength: 50,
        },
      },
      {
        name: 'versiRentPassword',
        label: 'VersiRent Test Account Password',
        type: 'password',
        required: true,
        placeholder: 'Enter VersiRent test password',
        helperText: 'Password for VersiRent test account',
        helpText: 'Enter the password for your VersiRent test account. This will be securely stored and used by Ideal staff and App Store reviewers to login and review the app. Test accounts MUST: (1) Have a balance owed greater than $0.00, (2) Must be a RTO or Club. The account should be kept active for as long as possible to ensure accessibility to reviewers and testers.',
        validation: {
          minLength: 6,
          maxLength: 100,
        },
      },
      {
        name: 'versiRentActiveAgreement',
        label: 'Test account has active agreement',
        type: 'checkbox',
        required: true,
        helperText: 'Confirm that your VersiRent test account has an active agreement',
        helpText: 'REQUIRED: Please confirm that your VersiRent test account meets ALL requirements: (1) Has a balance owed greater than $0.00, (2) Must be a RTO or Club, (3) Has an active agreement, and (4) Is configured as the proper test account type. This account will be used by Ideal staff and App Store reviewers to login and review the app, so it should be kept active for as long as possible to ensure accessibility. Using an incorrectly configured test account (e.g., real customer account, inactive agreement, wrong account type, wrong account category) will cause delays in internal review and app store submission.',
      },
      {
        name: 'appStoreContactEmail',
        label: 'App Store Contact Email',
        type: 'email',
        required: true,
        placeholder: 'contact@yourcompany.com',
        helperText: 'Email address that will be displayed in your app store listing',
        validation: {
          pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        },
      },
      {
        name: 'appStoreContactPhone',
        label: 'App Store Contact Phone',
        type: 'text',
        required: true,
        placeholder: '+1 (555) 123-4567',
        helperText: 'Phone number that will be displayed in your app store listing',
        validation: {
          minLength: 10,
          maxLength: 20,
        },
      },
      {
        name: 'testerEmails',
        label: 'Tester Email Addresses',
        type: 'multiemail',
        required: true,
        placeholder: 'tester1@example.com, tester2@example.com',
        helperText: 'Comma-separated list of email addresses for app testing',
        helpText: 'Enter email addresses of users who will test your app. Separate multiple emails with commas. These users will receive test builds.',
        validation: {
          minEmails: 1,
          maxEmails: 10,
        },
      },
      {
        name: 'googlePlayBrandAssetAgreement',
        label: 'Google Play Brand Asset Agreement',
        type: 'file',
        required: true,
        helperText: 'Upload the signed Google Play Brand Asset License Agreement PDF',
        helpText: 'This agreement is required for Google Play Store listing. Download the template from your welcome email, fill it out, sign it, and upload the completed PDF.',
        validation: {
          fileTypes: ['application/pdf'],
          maxFileSize: 10485760, // 10MB
        },
      },
      {
        name: 'appleDeveloperAccountName',
        label: 'Apple Developer Account Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your Apple Developer account name',
        helperText: 'Apple Developer account name for app store submission',
        helpText: 'Enter the exact name of your Apple Developer account as it appears in App Store Connect. This is used for app submission and must match your Apple Developer Program enrollment.',
        validation: {
          minLength: 2,
          maxLength: 100,
        },
      },
      {
        name: 'appleTeamId',
        label: 'Apple Team ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Apple Team ID',
        helperText: 'Your Apple Developer Team ID (found in App Store Connect)',
        helpText: 'Your Apple Team ID is a 10-character string found in App Store Connect under Membership Details. This is required for app provisioning and deployment.',
        validation: {
          minLength: 10,
          maxLength: 10,
          pattern: '^[A-Z0-9]{10}$',
        },
      },
      {
        name: 'appleAccountInvitesSection',
        label: 'Apple Account Invites',
        type: 'section',
        required: false,
        helperText: 'Please confirm that the following users have been added to your App Store Connect account',
        helpText: 'These users must be added to your App Store Connect account with the specified roles before proceeding. Admin users can manage the app, while Sales users have access to all apps for reporting purposes.',
      },
      {
        name: 'appleUserMwhorey',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'mwhorey@idealss.com (Admin role)',
      },
      {
        name: 'appleUserNmore',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'nmore@idealss.com (Admin role)',
      },
      {
        name: 'appleUserDev',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'dev@idealss.com (Admin role)',
      },
      {
        name: 'appleUserCstack',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'cstack@idealss.com (Admin role)',
      },
      {
        name: 'appleUserDscarafiotti',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'dscarafiotti@hotmail.com (Sales role with All Apps access)',
      },
      {
        name: 'teamKeyEnabled',
        label: 'Team Key Functionality Enabled',
        type: 'checkbox',
        required: true,
        helperText: 'Confirm that Team Key functionality has been enabled in your Apple Developer account',
        helpText: 'Team Key functionality allows multiple team members to access and manage your Apple Developer account. This must be enabled in your Apple Developer Program settings before app submission.',
      },
    ],
    steps: [
      {
        id: 'basic',
        title: 'Basic Information',
        description: 'Tell us about your app and company',
        fields: ['appName', 'logoUrl', 'appIconUrl', 'useLogoAsIcon', 'primaryColor', 'companyDescription', 'copyrightMsg'],
      },
      {
        id: 'appstore',
        title: 'App Store Information',
        description: 'App store listing details',
        fields: ['appStoreContactEmail', 'appStoreContactPhone', 'termsUrl', 'privacyUrl'],
      },
      {
        id: 'shop',
        title: 'Shop Configuration',
        description: 'E-commerce functionality setup',
        fields: ['ecommerceType', 'redirectWebsiteUrl', 'secondaryColor'],
      },
      {
        id: 'testing',
        title: 'Testing & Integration',
        description: 'Testing and third-party integration',
        sections: [
          {
            id: 'versirent-integration',
            title: 'VersiRent Integration',
            description: 'Configure your VersiRent test account',
            icon: '🔧',
            fields: ['versiRentStoreId', 'versiRentUsername', 'versiRentPassword', 'versiRentActiveAgreement'],
            order: 1,
          },
          {
            id: 'app-testing',
            title: 'App Testing',
            description: 'Set up app testing and distribution',
            icon: '🧪',
            fields: ['testerEmails'],
            order: 2,
          },
          {
            id: 'google-play-requirements',
            title: 'Google Play Requirements',
            description: 'Required agreements and documentation',
            icon: '📄',
            fields: ['googlePlayBrandAssetAgreement'],
            order: 3,
          },
        ],
        fields: ['versiRentStoreId', 'versiRentUsername', 'versiRentPassword', 'versiRentActiveAgreement', 'testerEmails', 'googlePlayBrandAssetAgreement'], // Fallback
      },
      {
        id: 'apple',
        title: 'Apple Developer Setup',
        description: 'Apple-specific requirements',
        sections: [
          {
            id: 'apple-developer-setup',
            title: 'Apple Developer Account',
            description: 'Your Apple Developer Program details',
            icon: '🍎',
            fields: ['appleDeveloperAccountName', 'appleTeamId'],
            order: 1,
          },
          {
            id: 'apple-account-invites',
            title: 'Apple Account Invites',
            description: 'Team members who need access to your Apple Developer account',
            icon: '👥',
            fields: ['appleUserMwhorey', 'appleUserNmore', 'appleUserDev', 'appleUserCstack', 'appleUserDscarafiotti'],
            order: 2,
          },
          {
            id: 'team-key-setup',
            title: 'Team Key Configuration',
            description: 'Enable team key functionality for collaborative development',
            icon: '🔑',
            fields: ['teamKeyEnabled'],
            order: 3,
          },
        ],
        fields: ['appleDeveloperAccountName', 'appleTeamId', 'appleUserMwhorey', 'appleUserNmore', 'appleUserDev', 'appleUserCstack', 'appleUserDscarafiotti', 'teamKeyEnabled'], // Fallback
      },
      {
        id: 'notifications',
        title: 'Notification Preferences',
        description: 'Communication preferences',
        fields: ['notificationEmail', 'enableEmailNotifications', 'notificationPhoneCountryCode', 'notificationPhone', 'enableSmsNotifications'],
      },
    ],
  },
  idealone: {
    fields: [
      {
        name: 'appName',
        label: 'Application Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your application name',
        helperText: 'This will be the name displayed to your users',
        validation: {
          minLength: 2,
          maxLength: 50,
        },
      },
      {
        name: 'logoUrl',
        label: 'Company Logo',
        type: 'file',
        required: true,
        helperText: 'Upload your company logo (PNG, JPG, SVG up to 5MB)',
        validation: {
          fileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
          maxFileSize: 5 * 1024 * 1024, // 5MB
        },
      },
      {
        name: 'useLogoAsIcon',
        label: 'Use logo as app icon',
        type: 'checkbox',
        required: false,
        helperText: 'Check this box to use your company logo as the app icon',
      },
      {
        name: 'appIconUrl',
        label: 'App Icon',
        type: 'file',
        required: true,
        helperText: 'Upload your app icon (PNG, JPG, SVG up to 2MB)',
        validation: {
          fileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
          maxFileSize: 2 * 1024 * 1024, // 2MB
        },
      },
      {
        name: 'primaryColor',
        label: 'Primary Brand Color',
        type: 'color',
        required: true,
        helperText: 'Select your primary brand color',
      },
      {
        name: 'secondaryColor',
        label: 'Secondary Brand Color',
        type: 'color',
        required: false,
        helperText: 'Select your secondary brand color (optional)',
      },
      {
        name: 'appStoreContactEmail',
        label: 'App Store Contact Email',
        type: 'email',
        required: true,
        placeholder: 'contact@yourcompany.com',
        helperText: 'Email address that will be displayed in your app store listing',
        validation: {
          pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        },
      },
      {
        name: 'appStoreContactPhone',
        label: 'App Store Contact Phone',
        type: 'text',
        required: true,
        placeholder: '+1 (555) 123-4567',
        helperText: 'Phone number that will be displayed in your app store listing',
        validation: {
          minLength: 10,
          maxLength: 20,
        },
      },
      {
        name: 'ecommerceType',
        label: 'E-commerce Solution',
        type: 'select',
        required: true,
        options: ['integrated', 'custom_in_app', 'custom_browser'],
        helperText: 'Choose how you want to handle e-commerce in your app',
        helpText: 'Integrated Shop provides a complete e-commerce solution, while custom options let you use your existing website.',
      },
      {
        name: 'redirectWebsiteUrl',
        label: 'Custom Shop URL',
        type: 'url',
        required: false, // Will be made conditional based on checkbox
        placeholder: 'yourwebsite.com or https://yourwebsite.com',
        helperText: 'Your website URL that will be used for shop features',
        helpText: 'This URL will be used for shop redirects and must be a valid URL. Only required if you checked the box above.',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+)',
        },
        conditional: {
          dependsOn: 'ecommerceType',
          showWhen: true,
        },
      },
      {
        name: 'termsUrl',
        label: 'Terms and Conditions',
        type: 'url',
        required: true,
        placeholder: 'https://example.com/terms',
        helperText: 'URL to your terms and conditions page or upload document',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+|file:.+)',
        },
      },
      {
        name: 'privacyUrl',
        label: 'Privacy Policy',
        type: 'url',
        required: true,
        placeholder: 'https://example.com/privacy',
        helperText: 'URL to your privacy policy page or upload document',
        validation: {
          pattern: '^(https?://.+|www\\..+|\\w+\\..+|file:.+)',
        },
      },
      {
        name: 'versiRentStoreId',
        label: 'VersiRent Store ID',
        type: 'text',
        required: false,
        placeholder: 'Enter VersiRent store ID',
        helperText: 'Store ID for your VersiRent test account (optional)',
        helpText: 'Enter the Store ID associated with your VersiRent test account. This helps identify which store/location the test account is for.',
        validation: {
          minLength: 1,
          maxLength: 50,
        },
      },
      {
        name: 'versiRentUsername',
        label: 'VersiRent Test Account Username',
        type: 'text',
        required: true,
        placeholder: 'Enter VersiRent test username',
        helperText: 'Username for VersiRent test account',
        helpText: 'Enter the username for your VersiRent test account. This account will be used by Ideal staff and App Store reviewers to login and review the app. Test accounts MUST: (1) Have a balance owed greater than $0.00, (2) Must be a RTO or Club. The account should be kept active for as long as possible to ensure accessibility to reviewers and testers.',
        validation: {
          minLength: 3,
          maxLength: 50,
        },
      },
      {
        name: 'versiRentPassword',
        label: 'VersiRent Test Account Password',
        type: 'password',
        required: true,
        placeholder: 'Enter VersiRent test password',
        helperText: 'Password for VersiRent test account',
        helpText: 'Enter the password for your VersiRent test account. This will be securely stored and used by Ideal staff and App Store reviewers to login and review the app. Test accounts MUST: (1) Have a balance owed greater than $0.00, (2) Must be a RTO or Club. The account should be kept active for as long as possible to ensure accessibility to reviewers and testers.',
        validation: {
          minLength: 6,
          maxLength: 100,
        },
      },
      {
        name: 'versiRentActiveAgreement',
        label: 'Test account has active agreement',
        type: 'checkbox',
        required: true,
        helperText: 'Confirm that your VersiRent test account has an active agreement',
        helpText: 'REQUIRED: Please confirm that your VersiRent test account meets ALL requirements: (1) Has a balance owed greater than $0.00, (2) Must be a RTO or Club, (3) Has an active agreement, and (4) Is configured as the proper test account type. This account will be used by Ideal staff and App Store reviewers to login and review the app, so it should be kept active for as long as possible to ensure accessibility. Using an incorrectly configured test account (e.g., real customer account, inactive agreement, wrong account type, wrong account category) will cause delays in internal review and app store submission.',
      },
      {
        name: 'appStoreContactEmail',
        label: 'App Store Contact Email',
        type: 'email',
        required: true,
        placeholder: 'contact@yourcompany.com',
        helperText: 'Email address that will be displayed in your app store listing',
        validation: {
          pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        },
      },
      {
        name: 'appStoreContactPhone',
        label: 'App Store Contact Phone',
        type: 'text',
        required: true,
        placeholder: '+1 (555) 123-4567',
        helperText: 'Phone number that will be displayed in your app store listing',
        validation: {
          minLength: 10,
          maxLength: 20,
        },
      },
      {
        name: 'testerEmails',
        label: 'Tester Email Addresses',
        type: 'multiemail',
        required: true,
        placeholder: 'tester1@example.com, tester2@example.com',
        helperText: 'Comma-separated list of email addresses for app testing',
        helpText: 'Enter email addresses of users who will test your app. Separate multiple emails with commas. These users will receive test builds.',
        validation: {
          minEmails: 1,
          maxEmails: 10,
        },
      },
      {
        name: 'googlePlayBrandAssetAgreement',
        label: 'Google Play Brand Asset Agreement',
        type: 'file',
        required: true,
        helperText: 'Upload the signed Google Play Brand Asset License Agreement PDF',
        helpText: 'This agreement is required for Google Play Store listing. Download the template from your welcome email, fill it out, sign it, and upload the completed PDF.',
        validation: {
          fileTypes: ['application/pdf'],
          maxFileSize: 10485760, // 10MB
        },
      },
      {
        name: 'appleDeveloperAccountName',
        label: 'Apple Developer Account Name',
        type: 'text',
        required: true,
        placeholder: 'Enter your Apple Developer account name',
        helperText: 'Apple Developer account name for app store submission',
        helpText: 'Enter the exact name of your Apple Developer account as it appears in App Store Connect. This is used for app submission and must match your Apple Developer Program enrollment.',
        validation: {
          minLength: 2,
          maxLength: 100,
        },
      },
      {
        name: 'appleTeamId',
        label: 'Apple Team ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Apple Team ID',
        helperText: 'Your Apple Developer Team ID (found in App Store Connect)',
        helpText: 'Your Apple Team ID is a 10-character string found in App Store Connect under Membership Details. This is required for app provisioning and deployment.',
        validation: {
          minLength: 10,
          maxLength: 10,
          pattern: '^[A-Z0-9]{10}$',
        },
      },
      {
        name: 'appleAccountInvitesSection',
        label: 'Apple Account Invites',
        type: 'section',
        required: false,
        helperText: 'Please confirm that the following users have been added to your App Store Connect account',
        helpText: 'These users must be added to your App Store Connect account with the specified roles before proceeding. Admin users can manage the app, while Sales users have access to all apps for reporting purposes.',
      },
      {
        name: 'appleUserMwhorey',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'mwhorey@idealss.com (Admin role)',
      },
      {
        name: 'appleUserNmore',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'nmore@idealss.com (Admin role)',
      },
      {
        name: 'appleUserDev',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'dev@idealss.com (Admin role)',
      },
      {
        name: 'appleUserCstack',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'cstack@idealss.com (Admin role)',
      },
      {
        name: 'appleUserDscarafiotti',
        label: '',
        type: 'checkbox',
        required: true,
        helperText: 'dscarafiotti@hotmail.com (Sales role with All Apps access)',
      },
      {
        name: 'teamKeyEnabled',
        label: 'Team Key Functionality Enabled',
        type: 'checkbox',
        required: true,
        helperText: 'Confirm that Team Key functionality has been enabled in your Apple Developer account',
        helpText: 'Team Key functionality allows multiple team members to access and manage your Apple Developer account. This must be enabled in your Apple Developer Program settings before app submission.',
      },
      {
        name: 'notificationPreferencesSection',
        label: 'Notification Preferences',
        type: 'section',
        required: false,
        helperText: 'Choose how you\'d like to receive updates about your project',
        helpText: 'We can send you notifications via email and/or SMS when your project status changes, when we need your input, or when there are any issues that require your attention.',
      },
      {
        name: 'notificationEmail',
        label: 'Notification Email Address',
        type: 'email',
        required: false,
        placeholder: 'notifications@yourcompany.com',
        helperText: 'Email address for project updates (optional)',
        helpText: 'Enter an email address where you\'d like to receive project updates. This can be different from your main contact email.',
        validation: {
          pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
        },
      },
      {
        name: 'enableEmailNotifications',
        label: 'Enable Email Notifications',
        type: 'checkbox',
        required: false,
        helperText: 'Receive project updates via email',
      },
      {
        name: 'notificationPhoneCountryCode',
        label: 'Country Code',
        type: 'select',
        required: false,
        helperText: 'Select your country code',
        options: ['+1', '+44', '+49', '+33', '+39', '+34', '+31', '+41', '+43', '+32', '+45', '+46', '+47', '+358', '+48', '+420', '+36', '+40', '+359', '+385', '+386', '+421', '+370', '+371', '+372', '+353', '+351', '+30', '+357', '+356', '+352', '+377', '+378', '+376', '+423', '+81', '+82', '+86', '+852', '+853', '+886', '+65', '+60', '+66', '+84', '+63', '+62', '+91', '+92', '+880', '+94', '+977', '+975', '+960', '+61', '+64', '+27', '+234', '+254', '+256', '+250', '+255', '+251', '+20', '+212', '+213', '+216', '+218', '+220', '+221', '+222', '+223', '+224', '+225', '+226', '+227', '+228', '+229', '+230', '+231', '+232', '+233', '+235', '+236', '+237', '+238', '+239', '+240', '+241', '+242', '+243', '+244', '+245', '+246', '+248', '+249', '+252', '+253', '+257', '+258', '+260', '+261', '+262', '+263', '+264', '+265', '+266', '+267', '+268', '+269', '+290', '+291', '+297', '+298', '+299', '+350', '+354', '+355', '+373', '+374', '+375', '+380', '+381', '+382', '+383', '+387', '+389', '+500', '+501', '+502', '+503', '+504', '+505', '+506', '+507', '+508', '+509', '+590', '+591', '+592', '+593', '+594', '+595', '+596', '+597', '+598', '+599', '+670', '+672', '+673', '+674', '+675', '+676', '+677', '+678', '+679', '+680', '+681', '+682', '+683', '+684', '+685', '+686', '+687', '+688', '+689', '+690', '+691', '+692', '+850', '+855', '+856', '+961', '+962', '+963', '+964', '+965', '+966', '+967', '+968', '+970', '+971', '+972', '+973', '+974', '+976', '+992', '+993', '+994', '+995', '+996', '+998'],
        helpText: 'Select your country code for SMS notifications.',
      },
      {
        name: 'notificationPhone',
        label: 'Notification Phone Number',
        type: 'text',
        required: false,
        placeholder: '(555) 123-4567',
        helperText: 'Phone number for SMS updates (optional)',
        helpText: 'Enter your phone number. It will be automatically formatted as you type.',
        validation: {
          pattern: '^[\\d\\s\\(\\)\\-]+$',
          minLength: 7,
        },
      },
      {
        name: 'enableSmsNotifications',
        label: 'Enable SMS Notifications',
        type: 'checkbox',
        required: false,
        helperText: 'Receive project updates via SMS',
      },
    ],
    steps: [
      {
        id: 'basic',
        title: 'Basic Information',
        description: 'Tell us about your app and company',
        fields: ['appName', 'logoUrl', 'appIconUrl', 'useLogoAsIcon', 'primaryColor', 'companyDescription', 'copyrightMsg'],
      },
      {
        id: 'appstore',
        title: 'App Store Information',
        description: 'App store listing details',
        fields: ['appStoreContactEmail', 'appStoreContactPhone', 'termsUrl', 'privacyUrl'],
      },
      {
        id: 'shop',
        title: 'Shop Configuration',
        description: 'E-commerce functionality setup',
        fields: ['ecommerceType', 'redirectWebsiteUrl', 'secondaryColor'],
      },
      {
        id: 'testing',
        title: 'Testing & Integration',
        description: 'Testing and third-party integration',
        sections: [
          {
            id: 'versirent-integration',
            title: 'VersiRent Integration',
            description: 'Configure your VersiRent test account',
            icon: '🔧',
            fields: ['versiRentStoreId', 'versiRentUsername', 'versiRentPassword', 'versiRentActiveAgreement'],
            order: 1,
          },
          {
            id: 'app-testing',
            title: 'App Testing',
            description: 'Set up app testing and distribution',
            icon: '🧪',
            fields: ['testerEmails'],
            order: 2,
          },
          {
            id: 'google-play-requirements',
            title: 'Google Play Requirements',
            description: 'Required agreements and documentation',
            icon: '📄',
            fields: ['googlePlayBrandAssetAgreement'],
            order: 3,
          },
        ],
        fields: ['versiRentStoreId', 'versiRentUsername', 'versiRentPassword', 'versiRentActiveAgreement', 'testerEmails', 'googlePlayBrandAssetAgreement'], // Fallback
      },
      {
        id: 'apple',
        title: 'Apple Developer Setup',
        description: 'Apple-specific requirements',
        sections: [
          {
            id: 'apple-developer-setup',
            title: 'Apple Developer Account',
            description: 'Your Apple Developer Program details',
            icon: '🍎',
            fields: ['appleDeveloperAccountName', 'appleTeamId'],
            order: 1,
          },
          {
            id: 'apple-account-invites',
            title: 'Apple Account Invites',
            description: 'Team members who need access to your Apple Developer account',
            icon: '👥',
            fields: ['appleUserMwhorey', 'appleUserNmore', 'appleUserDev', 'appleUserCstack', 'appleUserDscarafiotti'],
            order: 2,
          },
          {
            id: 'team-key-setup',
            title: 'Team Key Configuration',
            description: 'Enable team key functionality for collaborative development',
            icon: '🔑',
            fields: ['teamKeyEnabled'],
            order: 3,
          },
        ],
        fields: ['appleDeveloperAccountName', 'appleTeamId', 'appleUserMwhorey', 'appleUserNmore', 'appleUserDev', 'appleUserCstack', 'appleUserDscarafiotti', 'teamKeyEnabled'], // Fallback
      },
      {
        id: 'notifications',
        title: 'Notification Preferences',
        description: 'Communication preferences',
        fields: ['notificationEmail', 'enableEmailNotifications', 'notificationPhoneCountryCode', 'notificationPhone', 'enableSmsNotifications'],
      },
    ],
  },
};
